import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/prefs";
import { DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function BulkPriceUpdate({ ingredients, onDone }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({}); // ing_id -> new_price
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => { if (!open) setDrafts({}); }, [open]);

  const changed = Object.entries(drafts).filter(([_, v]) => v !== "" && v != null && !Number.isNaN(Number(v)));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { updates: changed.map(([id, price]) => ({ ingredient_id: id, purchase_price: Number(price) })) };
      const { data } = await api.post("/ingredients/bulk-price", payload);
      toast.success(`Updated ${data.updated} price${data.updated === 1 ? "" : "s"}`);
      onDone?.();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const filtered = ingredients.filter((i) => i.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="bulk-price-btn">
        <DollarSign className="w-4 h-4" /> Update prices
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-price-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Bulk price update</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Type only the ones that changed. Blank rows are skipped.</p>
          <Input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} className="mb-2" data-testid="bulk-price-search" />
          <div className="border border-border rounded overflow-hidden">
            <div className="overflow-x-auto max-h-[52vh] overflow-y-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead className="sticky top-0 bg-card">
                  <tr className="text-left border-b border-border">
                    <th className="px-3 py-2 label-eyebrow font-bold">Ingredient</th>
                    <th className="px-3 py-2 label-eyebrow font-bold text-right">Current pack price</th>
                    <th className="px-3 py-2 label-eyebrow font-bold text-right">New price</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((i) => (
                    <tr key={i.id} className="border-b border-border/50" data-testid={`bulk-row-${i.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{i.name}</div>
                        <div className="text-xs text-muted-foreground">{i.purchase_qty} {i.purchase_unit}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular text-muted-foreground">{formatMoney(i.purchase_price)}</td>
                      <td className="px-3 py-2">
                        <Input
                          type="number" step="0.01" placeholder="-"
                          value={drafts[i.id] ?? ""}
                          onChange={(e) => setDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                          className="text-right tabular ml-auto w-28"
                          data-testid={`bulk-input-${i.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground text-xs">No ingredients match.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !changed.length} className="gap-2" data-testid="bulk-price-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
              Update {changed.length} price{changed.length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
